import { ClubMembership, PassStatus, UserProfile } from "@/__generated__/graphql";
import PersonalAdmin from "@/components/cards/PersonalAdmin";
import { LessonsListView } from "@/components/lists/LessonsListView/LessonsListView";
import { PassesListView } from "@/components/lists/PassesListView/PassesListView";
import { Alert, Box, Button, Stack, Typography } from "@mui/joy";
import { FormattedDate, FormattedMessage } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import Stories from "../containers/Stories";
import CashbackView from "./CashbackView";
import GiftCertificateBanner from "../components/GiftCertificateBanner";
import { SocialActions } from "@/components/sections/SocialActions";

export default function HomeView({
  user,
  lessons,
  passes,
  clubMembership,
  allLessons,
  showAllLessons,
  onToggleShowAll,
}: {
  user: UserProfile;
  lessons: LinkedLesson[];
  passes: PartialPass[];
  clubMembership?: ClubMembership | null;
  allLessons: LinkedLesson[];
  showAllLessons: boolean;
  onToggleShowAll: () => void;
}) {
  return (
    <Stack gap={4} p={2} pt={4} pb={12}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography level="h3">
          <FormattedMessage defaultMessage="Здравствуйте, {name} 💕" values={{ name: user.name }} />
        </Typography>
      </Stack>

      <Stories />

      <GiftCertificateBanner />

      <Stack gap={2}>
        <Typography level="title-lg">
          <FormattedMessage defaultMessage="Ближайшие занятия" />
        </Typography>
        {lessons.length === 0 ? (
          <>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography color="neutral">
                <FormattedMessage defaultMessage="Нет записей" />
              </Typography>
            </Box>

            <Button variant="outlined" component={RouterLink} to="/">
              <FormattedMessage defaultMessage="Записаться" />
            </Button>
          </>
        ) : (
          <>
            <LessonsListView lessons={lessons} />
            {!showAllLessons && allLessons.length > 2 && (
              <Button variant="outlined" onClick={onToggleShowAll}>
                <FormattedMessage id="d1Vmwc" defaultMessage="Показать все записи" />
              </Button>
            )}
          </>
        )}
      </Stack>

      <Stack gap={2}>
        <Typography level="title-lg">
          <FormattedMessage defaultMessage="Ваши абонементы" />
        </Typography>
        <PassesListView passes={passes} />

        {passes.filter((pass) => ![PassStatus.Expired, PassStatus.NoHours, PassStatus.Refunded].includes(pass.status))
          .length === 0 && (
          <Alert variant="outlined" size="sm">
            <Typography level="body-sm">
              <FormattedMessage defaultMessage="Для оформления абонемента пожалуйста обратитесь к администратору" />
            </Typography>
          </Alert>
        )}
      </Stack>

      {clubMembership && (
        <Stack gap={2}>
          <Typography level="title-lg">
            <FormattedMessage defaultMessage="Ваша подписка AMANI CLUB" />
          </Typography>
          {clubMembership.active && clubMembership.expiresAt ? (
            <Alert variant="soft" color="success" size="sm">
              <Typography level="body-sm">
                <FormattedMessage
                  defaultMessage="Активна до {date}"
                  values={{
                    date: <FormattedDate value={clubMembership.expiresAt} day="numeric" month="long" year="numeric" />,
                  }}
                />
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert variant="outlined" size="sm">
                <Typography level="body-sm">
                  <FormattedMessage defaultMessage="Подписки нет — вступайте в AMANI Club, чтобы получить доступ к онлайн-практикам и лекциям" />
                </Typography>
              </Alert>
              <Button variant="outlined" component="a" href="https://club.amani-dance.com/season" target="_blank" rel="noopener noreferrer">
                <FormattedMessage defaultMessage="Узнать подробнее" />
              </Button>
            </>
          )}
        </Stack>
      )}

      {user ? <CashbackView user={user} /> : null}

      {user ? <PersonalAdmin user={user} /> : null}

      {user ? <SocialActions /> : null}
    </Stack>
  );
}
